"use client";
import { useState, useEffect, useRef, useId } from "react";
import { motion } from "framer-motion";

const RotatingWords = () => {
  const [currentTextIndex, setCurrentTextIndex] = useState(0);
  const texts = ["AI Agents", "AI Integrations", "End-to-End SaaS"];
  const [width, setWidth] = useState(100);
  const textRef = useRef<HTMLDivElement>(null);
  const id = useId();

  // Function to update width of the text for smooth transition
  const updateWidthForText = () => {
    if (textRef.current) {
      const textWidth = textRef.current.scrollWidth + 30; // Adding padding to width
      setWidth(textWidth);
    }
  };

  useEffect(() => {
    updateWidthForText();
  }, [currentTextIndex]);

  // Text switching logic with customized durations for smoother transitions
  useEffect(() => {
    const durations = [2000, 2000, 5000]; // Time durations for each rotating text
    let timeout: NodeJS.Timeout;

    const showNextText = () => {
      setCurrentTextIndex((prevIndex) => (prevIndex + 1) % texts.length);
    };

    timeout = setTimeout(showNextText, durations[currentTextIndex]);

    return () => clearTimeout(timeout);
  }, [currentTextIndex, texts.length]); // Re-run effect when currentTextIndex changes

  return (
    <motion.div
      layout
      layoutId={`words-here-${id}`}
      animate={{ width }}
      transition={{
        duration: 0.5,
        ease: "easeInOut", // Smoother ease-in-out effect for width change
      }}
      className="relative inline-block text-center text-3xl font-bold text-primary md:text-5xl dark:text-primary min-h-[1.2em] whitespace-nowrap bg-primary/10 dark:bg-primary/20 px-4 py-1 rounded-lg border border-primary/20 dark:border-primary/30 mt-2"
      key={texts[currentTextIndex]}
    >
      <motion.div
        ref={textRef}
        layoutId={`word-div-${texts[currentTextIndex]}-${id}`}
        transition={{
          duration: 0.4,
          ease: "easeInOut",
        }}
        className="inline-block"
      >
        {texts[currentTextIndex].split("").map((letter, index) => (
          <motion.span
            key={index}
            initial={{
              opacity: 0,
              filter: "blur(8px)", // Initial blur
            }}
            animate={{
              opacity: 1,
              filter: "blur(0px)", // Remove blur during animation
            }}
            transition={{
              duration: 0.4, // Slightly slower for the blur transition
              delay: index * 0.03, // Slightly longer delay between letters
              ease: "easeInOut", // Smoother easing for individual letters
            }}
            className="inline-block"
          >
            {letter === " " ? "\u00A0" : letter}
          </motion.span>
        ))}
      </motion.div>
    </motion.div>
  );
};

export default RotatingWords;
